<script setup lang="ts">
    import { getProductName } from '@shopware/helpers';
    import type { Schemas } from '#shopware';
    import { getProductRoute } from '#imports';

    const { product, seoLink = { id: '', link: '' } } = defineProps<{
        product: Schemas['Product'];
        seoLink?: {
            id: string;
            link: string;
        } | null;
    }>();
    const { hfpPushSuccess, hfpPushError } = useNotificationHelpers();
    const { getErrorsCodes } = useCartNotification();
    const { t } = useI18n();
    const { isCurrentUserParticipating } = useBonus();

    const { addToCart, isInCart, count, quantity } = useAddToCart(toRef(() => product));

    const properties = product.properties;
    const isOrganic = ref(false);
    const hasDiscount = ref(false);

    if (product.calculatedCheapestPrice?.unitPrice && product.calculatedPrice?.unitPrice) {
        if (product.calculatedPrice?.unitPrice - product.calculatedCheapestPrice?.unitPrice > 0) {
            hasDiscount.value = true;
        }
    }

    if (properties) {
        const ORGANIC_QUERY_VALUE = t('product.organicQueryValue');
        isOrganic.value = Boolean(
            properties.find(
                (property) => property.name === ORGANIC_QUERY_VALUE || property.translated?.name === ORGANIC_QUERY_VALUE
            )
        );
    }

    const addToCartProxy = async () => {
        let hasError = false;
        await addToCart();
        getErrorsCodes()?.forEach((element) => {
            if (element.key.includes('Mitarbeiterrabatt')) {
                return;
            }

            hasError = true;
            hfpPushError(
                t(`errors.${element.messageKey}`, {
                    ...element,
                    quantity: quantity.value,
                    name: product.name,
                    stock: product.stock
                })
            );
        });
        if (!hasError) {
            hfpPushSuccess(t(`cart.messages.addedToCart`, { p: product?.translated?.name }));
        }
    };

    const productDate = new Date(`${product.releaseDate}`);
    const daysPassed = ref(0);
    const dateNumber = Date.now() - productDate.getTime();
    const seconds = Math.floor(dateNumber / 1000);
    let interval = Math.floor(seconds / 31536000);
    interval = Math.floor(seconds / 86400);
    daysPassed.value = interval;

    const badges = ref(true);

    const imageElement = ref(null);
</script>

<template>
    <div
        class="product-card"
        data-testid="product-card"
    >
        <RouterLink
            :to="seoLink?.link ? useStoryblokUrl(seoLink.link) : getProductRoute(product)"
            class="--link-block"
            data-testid="product-box"
        >
            <div class="outer-image-wrapper">
                <div class="point-badge-wrapper">
                    <PointBadge
                        v-if="isCurrentUserParticipating && product.calculatedCheapestPrice?.unitPrice"
                        class="point-badge"
                        :points="calculatePointsFromPrice(product.calculatedCheapestPrice.unitPrice)"
                    ></PointBadge>
                </div>
                <!-- Image -->
                <div class="image-wrapper">
                    <div
                        v-if="badges || daysPassed <= 14 || hasDiscount"
                        class="badges"
                    >
                        <div class="top-badges">
                            <ProductDiscountBadge
                                v-if="hasDiscount"
                                :product="product"
                            />

                            <div
                                v-if="daysPassed <= 14"
                                class="new-badge"
                            >
                                {{ t('product.badge.new') }}
                            </div>

                            <img
                                v-if="isOrganic"
                                class="bio-badge"
                                src="@/assets/images/eu-organic-logo.jpg"
                                :alt="t('listing.bio')"
                            />
                        </div>
                        <div class="invader-wrapper">
                            <div
                                v-if="product?.stock > 0 && product?.stock < 26"
                                class="invader"
                            >
                                <p v-if="product?.stock <= 10">
                                    {{ t('product.badge.lowAmount', { p: product.stock }) }}
                                </p>
                                <p v-if="product?.stock >= 11 && product?.stock <= 25">
                                    {{ t('product.badge.lowStock') }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <ShopwarePicture
                        v-if="product.cover?.media?.url"
                        ref="imageElement"
                        class="product-image"
                        type="productCard"
                        :media="product.cover.media"
                        :alt="getProductName({ product }) || 'Kein Alt gepflegt'"
                        data-testid="product-box-img"
                    />

                    <img
                        v-else
                        ref="imageElement"
                        class="product-image"
                        src="@/assets/images/no-image.jpg"
                        alt="Kein Bild gepflegt"
                        data-testid="product-box-img"
                    />
                </div>
            </div>
            <!-- Title, Price and CTA -->
            <div class="content-wrapper">
                <div data-testid="product-box-product-name-link">
                    <h2>
                        {{ getProductName({ product }) }}
                    </h2>
                </div>
                <div class="details-wrapper">
                    <div class="detail">
                        <p
                            class="available"
                            :class="{
                                '--not-available': product.stock === 0,
                                '--few-available': product.stock < 10 && product.stock !== 0
                            }"
                        >
                            {{
                                product.stock >= 10
                                    ? t('product.available.available')
                                    : product.stock < 10 && product.stock !== 0
                                      ? t('product.available.fewAvailable')
                                      : t('product.available.notAvailable')
                            }}
                        </p>
                        <p
                            v-if="product.stock !== 0"
                            class="shipping"
                        >
                            {{ t('product.shipping.available') + ' ' }}
                            {{
                                product.deliveryTime?.name
                                    ? t('product.shipping.in') + ' ' + product.deliveryTime?.name
                                    : ''
                            }}
                        </p>
                    </div>
                </div>
            </div>
        </RouterLink>

        <div
            ref="buyButtonRef"
            class="cta-wrapper"
        >
            <SharedListingProductPrice
                :product="product"
                data-testid="product-box-product-price"
            />

            <PrimaryButton
                v-if="
                    !product.children?.length &&
                    (product.calculatedPrices?.length === 1 || product.calculatedPrices?.length === 0)
                "
                product-button
                :aria-label="count === 0 ? t('productCard.addToCart') : count + t('productCard.inCart')"
                data-testid="add-to-cart-button"
                :disabled="product.stock === 0"
                @click="addToCartProxy"
            >
                <span v-if="isInCart">
                    {{ count }}
                </span>

                <img
                    v-if="count === 0"
                    src="~/assets/icons/shopping-cart-white.svg"
                    :alt="t('productCard.altCart')"
                    aria-hidden="true"
                />

                <img
                    src="~/assets/icons/plus.svg"
                    :alt="t('productCard.altAdd')"
                    aria-hidden="true"
                />
            </PrimaryButton>

            <PrimaryButton
                v-else
                :button-text="t('productCard.detailPage')"
                :button-href="getProductRoute(product).path"
                has-icon
                force-product-styling
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/product-card/product-card.scss';
</style>
